import { APIRequestError } from '@/api/fetchers/APIRequestError';
import { systemErrorCodes } from '@/api/fetchers/codes';
import { getUrlFromFetchInput } from '@/utils/url';

export type FetchType = <T>(input: RequestInfo | URL, init?: RequestInit) => Promise<T | null>;

export const createApiFetcherWithErrorHandling = (
  fetcher: typeof fetch,
  _apiDomain: string | (() => any),
): FetchType => {
  return async <T>(
    _input: Parameters<typeof fetch>[0],
    init: Parameters<typeof fetch>[1],
  ): Promise<T | null> => {
    let input = _input;

    let url = getUrlFromFetchInput(input);
    const isRelativeUrl = !/^https?:\/\//gim.test(url);

    if (isRelativeUrl) {
      const apiDomain = typeof _apiDomain === 'function' ? _apiDomain() : _apiDomain;
      url = new URL(url, /^https?/gim.test(apiDomain) ? apiDomain : `https://${apiDomain}/`).toString();
      input = input instanceof Request ? new Request(url.toString(), input) : url;
    }

    const controller = new AbortController();
    const abortTimeout = setTimeout(() => controller.abort(), 2500);

    let response = await fetcher(input, { signal: controller.signal, ...init }).catch((e) => {
      let message = 'Failed to fetch';

      if (e.name === 'TimeoutError' || e.name === 'AbortError') {
        message = 'Failed to fetch due to timeout';
      } else if (e?.cause?.code && systemErrorCodes[e?.cause?.code]) {
        message = `Failed to fetch, ${systemErrorCodes[e?.cause?.code]}`;
      }

      throw new APIRequestError(message, { cause: e, url });
    });

    clearTimeout(abortTimeout);

    if (response?.ok) {
      return await response?.json().catch((e) => {
        throw new APIRequestError(`Unable to parse response body ${e?.message}`, {
          response,
          url,
          cause: e,
        });
      });
    }

    return await response
      .json()
      .catch((e) => {
        throw new APIRequestError('Unable to parse response error message', {
          response,
          url,
        });
      })
      .then((message) => {
        throw new APIRequestError(JSON.stringify(message), {
          response,
          url,
        });
      });
  };
};
